import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getLeads, assignLeads, sendLeadTexts, setLeadResult, getLeadMessages, saveCrew, saveRepPhone, getStaff } from '../utils/api';
import { rolesOf } from '../utils/people';
import { LEAD_CSS } from './BookAppointment';
import { PRODUCT_COLOR, time12, minutes, todayCA, addDays, dayLabel, relDay, RESULT, money, mapLink, fmtPhone } from '../utils/leads';

/* Dispatch Board — Joe hands out the day's appointments.
   Pick a rep for each appointment (or press Auto-assign to share them out evenly with no time clashes),
   add a second rep if needed, then "Save & text reps": each rep gets the details and a link to log the
   result, and homeowners who agreed get a "who's coming and when" text.                               */

const GAP = 120;   // minutes a rep needs between two appointments
const CSS = `
.dp-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px}
.dp-nav{display:flex;align-items:center;gap:6px;background:#fff;border:1px solid var(--line);border-radius:12px;padding:4px}
.dp-nav button{font:inherit;border:0;background:none;padding:7px 11px;border-radius:9px;cursor:pointer;font-weight:700;color:var(--ink)}
.dp-nav button:hover{background:var(--mist)}
.dp-nav button.on{background:var(--forest);color:#fff}
.dp-nav input{font:inherit;border:0;font-weight:700;padding:6px}
.dp-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px}
.dp-stat{background:#fff;border:1px solid var(--line);border-radius:14px;padding:13px 16px}
.dp-stat b{display:block;font-family:'Fraunces',Georgia,serif;font-size:28px;line-height:1.1}
.dp-stat span{font-size:12.5px;color:var(--muted);font-weight:600}
.dp-stat.main{background:var(--forest);border:0}.dp-stat.main b{color:#F0C842}.dp-stat.main span{color:rgba(255,255,255,.78)}
.dp-stat.warn{background:#FFF8E6;border-color:#E8B04A}.dp-stat.warn b{color:#8A5A00}
.dp-reps{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0 0}
.dp-rep{font:inherit;font-size:13.5px;font-weight:700;border:1.5px solid var(--line);background:#fff;border-radius:999px;padding:6px 8px 6px 13px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;color:var(--ink)}
.dp-rep em{font-style:normal;font-size:12px;background:var(--mist);color:var(--muted);border-radius:999px;padding:1px 8px;min-width:12px;text-align:center}
.dp-rep.on{border-color:var(--forest);background:#EAF3EE}
.dp-rep.on em{background:var(--forest);color:#fff}
.dp-rep.off{opacity:.5;text-decoration:line-through}
.dp-rep .nophone{color:#B35C00;font-size:11px}
.dp-list{display:flex;flex-direction:column;gap:10px;margin-top:16px}
.dp-card{display:grid;grid-template-columns:104px minmax(0,1fr) 300px;gap:16px;background:#fff;border:1px solid var(--line);border-radius:16px;padding:16px 18px;border-left:6px solid var(--line)}
.dp-card.need{border-left-color:#E0A021}
.dp-card.ok{border-left-color:#2E9B5F}
.dp-card.res{border-left-color:#0D3B2E}
.dp-time{font-family:'Fraunces',Georgia,serif;font-weight:800;font-size:20px;line-height:1.1;white-space:nowrap}
.dp-time small{display:block;font:600 12px 'Inter',sans-serif;color:var(--muted);margin-top:4px}
.dp-cust{font-weight:800;font-size:16px}
.dp-addr{font-size:13.5px;color:var(--muted);margin-top:2px}
.dp-addr a{color:var(--pine)}
.dp-tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.dp-tag{font-size:12px;font-weight:700;border-radius:6px;padding:2px 8px;border:1px solid rgba(0,0,0,.08)}
.dp-meta{font-size:12.5px;color:var(--muted);margin-top:8px;line-height:1.55}
.dp-note{font-size:13px;background:var(--mist);border-radius:8px;padding:7px 10px;margin-top:8px}
.dp-assign{display:flex;flex-direction:column;gap:8px}
.dp-sel{display:flex;gap:6px;align-items:center}
.dp-sel select{flex:1;font:inherit;font-size:14px;font-weight:600;padding:9px 10px;border:1.5px solid var(--line);border-radius:10px;background:#fff;min-width:0}
.dp-sel select.empty{border-color:#E0A021;background:#FFFBF0}
.dp-sel select.clash{border-color:#D2453B;background:#FDF0EE}
.dp-x{font:inherit;border:0;background:var(--mist);width:30px;height:30px;border-radius:50%;cursor:pointer;color:var(--muted);flex-shrink:0}
.dp-add{font:inherit;font-size:13px;font-weight:700;border:1.5px dashed var(--line);background:none;border-radius:10px;padding:7px;cursor:pointer;color:var(--pine)}
.dp-add:hover{border-color:var(--forest)}
.dp-warn{font-size:12px;color:#A3322A;font-weight:600}
.dp-state{font-size:12px;font-weight:700;display:flex;align-items:center;gap:6px}
.dp-res select{font:inherit;font-size:13px;padding:7px 9px;border:1.5px solid var(--line);border-radius:9px;width:100%}
.dp-save{position:sticky;bottom:12px;z-index:20;margin-top:16px;background:#13211B;color:#fff;border-radius:16px;padding:12px 16px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;box-shadow:0 18px 40px -18px rgba(0,0,0,.6)}
.dp-save span{flex:1;font-size:14px;min-width:200px}
.dp-save .lx-btn{border-color:#fff}
.dp-save .lx-btn.main{background:#F0C842;border-color:#F0C842;color:#13211B}
.dp-drawer-wrap{position:fixed;inset:0;z-index:1000;background:rgba(9,30,23,.42);display:flex;justify-content:flex-end}
.dp-drawer{width:min(620px,100%);height:100%;background:#F7F9F8;overflow-y:auto;animation:dpIn .22s ease}
.dp-drawer header{position:sticky;top:0;z-index:2;background:#0D3B2E;color:#fff;padding:18px 22px;display:flex;justify-content:space-between;align-items:center}
.dp-drawer header h3{margin:0;font-family:'Fraunces',Georgia,serif;font-size:22px;color:#fff}
.dp-drawer header button{width:34px;height:34px;border-radius:50%;border:1px solid rgba(255,255,255,.4);background:none;color:#fff;cursor:pointer}
.dp-db{padding:18px 22px 30px;display:flex;flex-direction:column;gap:12px}
.dp-msg{background:#fff;border:1px solid var(--line);border-radius:12px;padding:12px 14px}
.dp-msg pre{white-space:pre-wrap;font:13px/1.5 'Inter',sans-serif;margin:8px 0;background:var(--mist);border-radius:8px;padding:10px}
.dp-msg .acts{display:flex;gap:8px;flex-wrap:wrap}
.dp-msg .acts a,.dp-msg .acts button{font:inherit;font-size:12.5px;font-weight:700;border:1.5px solid var(--forest);color:var(--forest);background:#fff;border-radius:8px;padding:5px 10px;cursor:pointer;text-decoration:none}
.dp-banner{background:#FFF8E6;border:1px solid #E8B04A;border-radius:12px;padding:12px 14px;font-size:13.5px;line-height:1.5}
.dp-banner.ok{background:#E3F3EA;border-color:#9CD3B2}
@keyframes dpIn{from{transform:translateX(30px);opacity:.5}}
@media(max-width:1000px){.dp-card{grid-template-columns:76px minmax(0,1fr)}.dp-assign{grid-column:1 / -1}.dp-stats{grid-template-columns:1fr 1fr}}
@media(max-width:700px){.dp-card{grid-template-columns:1fr;gap:10px}.dp-time{display:flex;gap:10px;align-items:baseline}.dp-time small{margin-top:0}
  .dp-head{height:auto!important;flex-wrap:wrap;padding-top:10px!important;padding-bottom:10px!important;gap:8px}.dp-head .lx-btn{padding:7px 10px!important;font-size:13px}.dp-nav{width:100%;box-sizing:border-box}.dp-nav input{min-width:0;flex:1;width:0}.dp-nav button{padding:7px 8px}}
`;
const STATE = {
  need: { t: 'Needs a rep', c: '#8A5A00' }, draft: { t: 'Not texted yet', c: '#8A5A00' }, changed: { t: 'Changed, text again', c: '#A3322A' },
  sent: { t: '✓ Texted', c: '#1F6B43' },
};

function Drawer({ title, onClose, children }) {
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  return (
    <div className="dp-drawer-wrap" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className="dp-drawer" role="dialog" aria-label={title}><header><h3>{title}</h3><button type="button" onClick={onClose} aria-label="Close">✕</button></header><div className="dp-db">{children}</div></aside>
    </div>
  );
}

export default function Dispatch() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayCA());
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [draft, setDraft] = useState({});            // lead id → [rep names]
  const [off, setOff] = useState({});                // rep name → true when not working that day
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(null);        // 'texts' | 'crews' | 'phones'
  const [msgs, setMsgs] = useState(null);

  const load = useCallback(() => getLeads(date, date).then(d => {
    setData(d);
    setDraft(Object.fromEntries(d.leads.map(l => [l.id, l.reps])));
  }).catch(() => toast.error('Could not load the appointments')), [date]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { getStaff().then(setStaff).catch(() => {}); }, []);
  const loadMsgs = useCallback(() => getLeadMessages(date).then(setMsgs).catch(() => {}), [date]);
  useEffect(() => { if (drawer === 'texts') loadMsgs(); }, [drawer, loadMsgs]);

  const leads = data?.leads || [];
  const salesReps = useMemo(() => staff.filter(s => (s.status || 'Active') === 'Active' && rolesOf(s).includes('Sales Rep')).map(s => s.name).sort(), [staff]);
  const phoneOf = n => (data?.phones || []).find(p => p.name === n)?.phone || '';
  const working = salesReps.filter(r => !off[r]);
  const load_ = useMemo(() => { const m = {}; Object.values(draft).forEach(rs => rs.forEach(r => { if (r) m[r] = (m[r] || 0) + 1; })); return m; }, [draft]);
  const changed = leads.filter(l => JSON.stringify((draft[l.id] || []).filter(Boolean)) !== JSON.stringify(l.reps));
  const unsent = leads.filter(l => { const d = (draft[l.id] || []).filter(Boolean); return d.length && d.some(r => !l.sent_reps.includes(r)); });
  const needRep = leads.filter(l => !(draft[l.id] || []).filter(Boolean).length && !['Ctc', 'Ctp', 'Rescheduled'].includes(l.disp));

  // clashes: same rep within GAP minutes on another appointment that day
  const clashOf = (lead, rep) => rep && leads.some(o => o.id !== lead.id && (draft[o.id] || []).includes(rep) && Math.abs(minutes(o.appt_time) - minutes(lead.appt_time)) < GAP);
  const setRep = (id, i, name) => setDraft(d => { const a = [...(d[id] || [])]; a[i] = name; return { ...d, [id]: a }; });
  const addRep = id => setDraft(d => ({ ...d, [id]: [...(d[id] || []), ''] }));
  const dropRep = (id, i) => setDraft(d => ({ ...d, [id]: (d[id] || []).filter((_, k) => k !== i) }));

  function autoAssign() {
    if (!working.length) return toast.error('Turn on at least one rep who is working');
    const next = { ...draft }; const count = {};
    Object.values(next).forEach(rs => rs.forEach(r => { if (r) count[r] = (count[r] || 0) + 1; }));
    const busyAt = rep => Object.entries(next).filter(([, rs]) => rs.includes(rep)).map(([id]) => minutes(leads.find(l => String(l.id) === String(id))?.appt_time));
    let filled = 0, forced = 0;
    for (const l of [...leads].sort((a, b) => minutes(a.appt_time) - minutes(b.appt_time))) {
      if ((next[l.id] || []).filter(Boolean).length || ['Ctc', 'Ctp', 'Rescheduled'].includes(l.disp) || l.disp) continue;
      const t = minutes(l.appt_time);
      const free = working.filter(r => busyAt(r).every(x => Math.abs(x - t) >= GAP));
      const pool = free.length ? free : working;
      const low = Math.min(...pool.map(r => count[r] || 0));
      const pick = pool.filter(r => (count[r] || 0) === low)[(l.id * 7) % pool.filter(r => (count[r] || 0) === low).length];
      next[l.id] = [pick]; count[pick] = (count[pick] || 0) + 1; filled++; if (!free.length) forced++;
    }
    setDraft(next);
    toast.success(filled ? `Assigned ${filled} appointment${filled === 1 ? '' : 's'}${forced ? ` (${forced} with a time clash, check the red ones)` : ''}. Review, then save.` : 'Every appointment already has a rep', { duration: 5000 });
  }

  async function save(andText) {
    setBusy(true);
    try {
      if (changed.length) await assignLeads(changed.map(l => ({ id: l.id, reps: (draft[l.id] || []).filter(Boolean) })));
      if (andText) {
        const r = await sendLeadTexts(date);
        setMsgs({ messages: r.messages, texting: r.texting });
        toast.success(r.texting ? `Texted ${r.reps} rep message${r.reps === 1 ? '' : 's'} and ${r.owners} homeowner${r.owners === 1 ? '' : 's'}` : `${r.reps + r.owners} texts written. Texting isn't connected yet, so they're waiting in Texts.`, { duration: 6000 });
        setDrawer('texts');
      } else toast.success('Saved');
      await load();
    } catch (x) { toast.error(x.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  }
  async function result(l, disp) {
    let amount = 0;
    if (disp === '*SOLD*') { const a = window.prompt(`Sale amount for ${l.customer}?`, l.amount || ''); if (a == null) return; amount = a; }
    try { await setLeadResult(l.id, { disp, amount, notes: l.result_notes || '' }); toast.success('Result saved'); load(); }
    catch (x) { toast.error(x.response?.data?.error || 'Could not save the result'); }
  }

  const stateOf = l => { const d = (draft[l.id] || []).filter(Boolean);
    if (!d.length) return 'need';
    if (JSON.stringify(d) !== JSON.stringify(l.reps)) return l.sent_reps.length ? 'changed' : 'draft';
    return d.every(r => l.sent_reps.includes(r)) ? 'sent' : 'draft'; };
  const past = date < todayCA();

  return (
    <div className="lx">
      <style>{LEAD_CSS + CSS}</style>
      <div className="page-header dp-head">
        <h2>Dispatch Board</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="lx-btn" style={{ padding: '8px 13px' }} onClick={() => setDrawer('texts')}>Texts</button>
          <button type="button" className="lx-btn" style={{ padding: '8px 13px' }} onClick={() => setDrawer('phones')}>Rep phones</button>
          <button type="button" className="lx-btn" style={{ padding: '8px 13px' }} onClick={() => setDrawer('crews')}>Crews</button>
          <Link to="/book" className="lx-btn main" style={{ padding: '8px 13px', textDecoration: 'none' }}>+ Book</Link>
        </div>
      </div>
      <div className="page-body">
        <div className="dp-bar">
          <div className="dp-nav">
            <button type="button" onClick={() => setDate(addDays(date, -1))} aria-label="Previous day">‹</button>
            <button type="button" className={date === todayCA() ? 'on' : ''} onClick={() => setDate(todayCA())}>Today</button>
            <button type="button" className={date === addDays(todayCA(), 1) ? 'on' : ''} onClick={() => setDate(addDays(todayCA(), 1))}>Tomorrow</button>
            <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} aria-label="Pick a day" />
            <button type="button" onClick={() => setDate(addDays(date, 1))} aria-label="Next day">›</button>
          </div>
          <h3 style={{ margin: 0, fontSize: 20 }}>{dayLabel(date)}</h3>
        </div>

        <div className="dp-stats">
          <div className="dp-stat main"><b>{leads.length}</b><span>appointments</span></div>
          <div className={'dp-stat' + (needRep.length ? ' warn' : '')}><b>{needRep.length}</b><span>still need a rep</span></div>
          <div className="dp-stat"><b>{leads.filter(l => stateOf(l) === 'sent').length}</b><span>reps texted</span></div>
          <div className="dp-stat"><b>{leads.filter(l => l.disp === '*SOLD*').length}</b><span>sold · {money(leads.filter(l => l.disp === '*SOLD*').reduce((s, l) => s + (l.amount || 0), 0))}</span></div>
        </div>

        {!past && (
          <div className="lx-card" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <h3 style={{ margin: 0, fontSize: 17 }}>Reps working {relDay(date).toLowerCase() === 'today' || relDay(date) === 'Tomorrow' ? relDay(date).toLowerCase() : 'this day'}</h3>
                <div className="lx-sub">Tap a rep to take them off for the day. The number is how many appointments they have.</div>
              </div>
              <button type="button" className="lx-btn main" onClick={autoAssign} disabled={!leads.length}>Auto-assign</button>
            </div>
            <div className="dp-reps">{salesReps.map(r => (
              <button key={r} type="button" className={'dp-rep' + (off[r] ? ' off' : load_[r] ? ' on' : '')} onClick={() => setOff(o => ({ ...o, [r]: !o[r] }))} title={off[r] ? 'Off today' : 'Working'}>
                {r}{!phoneOf(r) && <span className="nophone">no phone</span>}<em>{load_[r] || 0}</em></button>))}
              {!salesReps.length && <span className="lx-sub">No active staff with the Sales Rep role yet. Add the role on Staff Profiles.</span>}</div>
          </div>
        )}

        {!data ? <div className="loader"><div className="spinner" /></div>
          : !leads.length ? <div className="lx-card" style={{ marginTop: 16, textAlign: 'center', color: '#5E6E66' }}>No appointments booked for {dayLabel(date)}. <Link to="/book">Book one</Link>.</div>
          : (
            <div className="dp-list">
              {leads.map(l => {
                const d = draft[l.id] && draft[l.id].length ? draft[l.id] : [''];
                const st = stateOf(l); const R = RESULT[l.disp];
                return (
                  <div key={l.id} className={'dp-card ' + (l.disp ? 'res' : st === 'need' ? 'need' : st === 'sent' ? 'ok' : '')}>
                    <div className="dp-time">{time12(l.appt_time)}<small>{l.appt_type}</small></div>
                    <div style={{ minWidth: 0 }}>
                      <div className="dp-cust">{l.customer}{l.customer_2 ? ` & ${l.customer_2}` : ''}</div>
                      <div className="dp-addr"><a href={mapLink(l)} target="_blank" rel="noreferrer">{l.address}{l.city ? `, ${l.city}` : ''}{l.zip ? ` ${l.zip}` : ''}</a> · {fmtPhone(l.phone)}</div>
                      <div className="dp-tags">{l.products.map(p => <span key={p} className="dp-tag" style={{ background: PRODUCT_COLOR[p] || '#eee' }}>{p}</span>)}{l.meet_with && <span className="dp-tag" style={{ background: '#fff' }}>Meet {l.meet_with}</span>}{l.sms_ok && <span className="dp-tag" style={{ background: '#fff' }}>📱 OK to text</span>}</div>
                      <div className="dp-meta">Set by <b>{l.set_by || '—'}</b>{l.crew_name ? ` · ${l.crew_name}` : ''}{l.source ? ` · ${l.source}` : ''} · confirmed by <b>{l.confirmer || '—'}</b></div>
                      {l.notes && <div className="dp-note">{l.notes}</div>}
                      {l.result_notes && <div className="dp-note" style={{ background: '#EAF3EE' }}><b>Rep:</b> {l.result_notes}</div>}
                    </div>
                    <div className="dp-assign">
                      {d.map((r, i) => (
                        <div key={i} className="dp-sel">
                          <select value={r} className={!r ? 'empty' : clashOf(l, r) ? 'clash' : ''} onChange={e => setRep(l.id, i, e.target.value)} aria-label={i ? 'Second rep' : 'Sales rep'}>
                            <option value="">{i ? 'Second rep…' : 'Choose a rep…'}</option>
                            {[...new Set([...salesReps, r].filter(Boolean))].filter(n => n === r || !d.includes(n)).map(n => <option key={n} value={n}>{n}{off[n] ? ' (off)' : ''} · {load_[n] || 0}</option>)}
                          </select>
                          {(i > 0 || r) && <button type="button" className="dp-x" onClick={() => (i ? dropRep(l.id, i) : setRep(l.id, 0, ''))} aria-label="Remove rep">✕</button>}
                        </div>))}
                      {d.some(r => clashOf(l, r)) && <div className="dp-warn">⚠ {d.filter(r => clashOf(l, r)).join(', ')} has another appointment within {GAP / 60} hours</div>}
                      {d[0] && d.length < 3 && <button type="button" className="dp-add" onClick={() => addRep(l.id)}>+ Add another rep</button>}
                      <div className="dp-state" style={{ color: STATE[st].c }}>{STATE[st].t}</div>
                      {(l.reps.length > 0 || l.disp) && (
                        <div className="dp-res">
                          {R ? <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><span className="lx-pill" style={{ background: R.color, color: R.fg }}>{R.label}{l.disp === '*SOLD*' ? ` · ${money(l.amount)}` : ''}</span><span className="lx-sub">{l.result_by ? `by ${l.result_by}` : ''}</span></div> : null}
                          <select value="" onChange={e => e.target.value && result(l, e.target.value)} style={{ marginTop: 6 }}>
                            <option value="">{R ? 'Change result…' : 'Enter result (office)…'}</option>
                            {Object.entries(RESULT).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                          {l.disp === '*SOLD*' && (l.job_id
                            ? <div className="lx-sub" style={{ marginTop: 6 }}>✓ Job record created</div>
                            : <button type="button" className="lx-btn main" style={{ marginTop: 8, width: '100%', padding: '9px' }} onClick={() => navigate(`/add?lead=${l.id}`)}>Create job record →</button>)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {(changed.length > 0 || unsent.length > 0) && (
          <div className="dp-save">
            <span>{changed.length ? `${changed.length} change${changed.length === 1 ? '' : 's'} not saved` : 'Saved'}{unsent.length ? ` · ${unsent.length} appointment${unsent.length === 1 ? '' : 's'} not texted yet` : ''}</span>
            {changed.length > 0 && <button type="button" className="lx-btn" disabled={busy} onClick={() => save(false)}>Save</button>}
            <button type="button" className="lx-btn main" disabled={busy || !unsent.length} onClick={() => save(true)}>{busy ? 'Working…' : `Save & text reps (${unsent.length})`}</button>
          </div>
        )}
      </div>

      {drawer === 'texts' && (
        <Drawer title={`Texts · ${relDay(date)}`} onClose={() => setDrawer(null)}>
          {msgs && (msgs.texting
            ? <div className="dp-banner ok">Texting is connected. Messages go out as soon as you press "Save &amp; text reps".</div>
            : <div className="dp-banner"><b>Texting isn't connected yet.</b> Every message is written and saved here. Until the Twilio number is set up, use <b>Copy</b> or <b>Text from my phone</b> to send it yourself.</div>)}
          {!msgs ? <div className="loader"><div className="spinner" /></div> : !msgs.messages.length ? <div className="lx-sub">No texts for this day yet.</div>
            : msgs.messages.map(m => (
              <div key={m.id} className="dp-msg">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <b>{m.to_kind === 'rep' ? '👔 ' : '🏠 '}{m.to_name} <span className="lx-sub">{m.to_phone || 'no phone number'}</span></b>
                  <span className="lx-pill" style={{ background: { sent: '#E3F3EA', failed: '#FBE4E1', no_phone: '#FBE4E1', ready: '#FFF1D6' }[m.status], color: { sent: '#1F6B43', failed: '#A3322A', no_phone: '#A3322A', ready: '#8A5A00' }[m.status] }}>
                    {{ sent: '✓ Sent', failed: 'Failed', no_phone: 'No phone number', ready: 'Ready to send' }[m.status]}</span>
                </div>
                {m.error && <div className="dp-warn">{m.error}</div>}
                <pre>{m.body}</pre>
                <div className="acts">
                  <button type="button" onClick={() => navigator.clipboard?.writeText(m.body).then(() => toast.success('Copied'))}>Copy</button>
                  {m.to_phone && <a href={`sms:${m.to_phone}?&body=${encodeURIComponent(m.body)}`}>Text from my phone</a>}
                </div>
              </div>))}
        </Drawer>
      )}
      {drawer === 'phones' && <PhonesDrawer staff={staff} reps={salesReps} phones={data?.phones || []} onSaved={p => setData(d => ({ ...d, phones: p }))} onClose={() => setDrawer(null)} />}
      {drawer === 'crews' && <CrewsDrawer crews={data?.crews || []} staff={staff} onSaved={c => setData(d => ({ ...d, crews: c }))} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function PhonesDrawer({ staff, reps, phones, onSaved, onClose }) {
  const [val, setVal] = useState(() => Object.fromEntries(phones.map(p => [p.name, p.phone || ''])));
  const save = async name => {
    const s = staff.find(x => x.name === name); if (!s) return;
    if ((phones.find(p => p.name === name)?.phone || '') === (val[name] || '')) return;
    try { const r = await saveRepPhone(s.id, fmtPhone(val[name])); onSaved(r.phones); setVal(v => ({ ...v, [name]: fmtPhone(v[name]) })); toast.success(`Saved ${name}'s phone`); }
    catch (x) { toast.error(x.response?.data?.error || 'Could not save'); }
  };
  return (
    <Drawer title="Rep phones" onClose={onClose}>
      <div className="dp-banner ok">Reps get their appointment texts on these numbers. Saved as soon as you leave the box.</div>
      <div className="lx-card" style={{ padding: 12 }}>{reps.map(n => (
        <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 4px', borderBottom: '1px solid #EEF2EF' }}>
          <b style={{ flex: 1 }}>{n}</b>
          <input className="lx-in" style={{ width: 190 }} type="tel" placeholder="(909) 555-0100" value={val[n] || ''} onChange={e => setVal(v => ({ ...v, [n]: e.target.value }))} onBlur={() => save(n)} />
        </div>))}</div>
    </Drawer>
  );
}

function CrewsDrawer({ crews, staff, onSaved, onClose }) {
  const [edit, setEdit] = useState(null);
  const active = staff.filter(s => (s.status || 'Active') === 'Active');
  const canvassers = active.filter(s => rolesOf(s).some(r => ['Canvasser', 'Crew Chief'].includes(r))).map(s => s.name).sort();
  const everyone = active.map(s => s.name).sort();
  const save = async () => {
    try { onSaved(await saveCrew(edit)); setEdit(null); toast.success('Crew saved'); } catch (x) { toast.error(x.response?.data?.error || 'Could not save'); }
  };
  return (
    <Drawer title="Crews" onClose={onClose}>
      <div className="dp-banner ok">A crew is a van with its crew chief and canvassers. When a confirmer picks the canvasser, the crew fills in by itself. The Performance page judges each crew.</div>
      {edit ? (
        <div className="lx-card">
          <div className="lx-grid">
            <div className="lx-f w3"><label>Crew name <b>*</b></label><input className="lx-in" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} placeholder="Van 1" /></div>
            <div className="lx-f w3"><label>Van</label><input className="lx-in" value={edit.van} onChange={e => setEdit({ ...edit, van: e.target.value })} placeholder="White Ford Transit" /></div>
            <div className="lx-f w6"><label>Crew chief</label><select className="lx-in" value={edit.chief} onChange={e => setEdit({ ...edit, chief: e.target.value })}><option value="">Choose…</option>{everyone.map(n => <option key={n}>{n}</option>)}</select></div>
            <div className="lx-f w6"><label>Canvassers in this crew</label>
              <div className="lx-chips">{canvassers.map(n => <button key={n} type="button" className={'lx-chip' + (edit.members.includes(n) ? ' on' : '')} onClick={() => setEdit({ ...edit, members: edit.members.includes(n) ? edit.members.filter(x => x !== n) : [...edit.members, n] })}>{n}</button>)}
                {!canvassers.length && <span className="lx-sub">No staff with the Canvasser role yet.</span>}</div></div>
            <label className="lx-check" style={{ gridColumn: '1 / -1' }}><input type="checkbox" checked={edit.active} onChange={e => setEdit({ ...edit, active: e.target.checked })} /><span>Crew is active</span></label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}><button type="button" className="lx-btn main" onClick={save}>Save crew</button><button type="button" className="lx-btn" onClick={() => setEdit(null)}>Cancel</button></div>
        </div>
      ) : <button type="button" className="lx-btn main" onClick={() => setEdit({ name: '', van: '', chief: '', members: [], active: true })}>+ Add a crew</button>}
      {crews.map(c => (
        <div key={c.id} className="dp-msg" style={{ cursor: 'pointer', opacity: c.active ? 1 : 0.55 }} onClick={() => setEdit({ ...c })}>
          <b>{c.name}</b>{c.van ? <span className="lx-sub"> · {c.van}</span> : ''}{!c.active && <span className="lx-sub"> · inactive</span>}
          <div className="lx-sub" style={{ marginTop: 4 }}>Chief: {c.chief || '—'} · {c.members.length ? c.members.join(', ') : 'no canvassers yet'}</div>
        </div>))}
    </Drawer>
  );
}
